import React, { FC, useState } from 'react'

import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { Control, Controller } from 'react-hook-form'

import CloseIcon from 'src/Assets/Svg/common/cross.svg?react'
import Arrow from 'src/Assets/Svg/common/arrowLeft.svg?react'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import { IconButton } from 'src/Components/Buttons/IconButton'
import { TrackAudioPlayer } from 'src/Components/TrackAudioPlayer'

import { MobileSearchInput } from '../MobileSearchInput'
import { SearchTrackFormData, TrackSearchProps } from '../../index'
import { Loading } from '../Loading'
import { TrackItem } from '../TrackItem'

const TracksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 8px);
  padding-top: 56px;
`

const Results = styled.div`
  display: grid;
  gap: 4px;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  margin-top: 8px;
  margin-bottom: 8px;
  &::-webkit-scrollbar {
    width: 8px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    height: 20px;
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 40px;
    background-color: ${({ theme }) => theme.color.mediumGrey};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.color.mediumGrey};
    width: 8px !important;
  }

  scrollbar-color: ${({ theme }) => theme.color.mediumGrey} transparent;

  & {
    -ms-overflow-style: none;
  }

  &::-ms-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-ms-track {
    background-color: transparent;
  }

  &::-ms-thumb {
    border-radius: 40px;
    background-color: ${({ theme }) => theme.color.mediumGrey};
  }
`

const StyledIconButton = styled(IconButton)`
  border: none;
`

const MobileInputContainer = styled.div`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 8px;
  align-items: center;
  gap: 4px;
  position: fixed;
  z-index: 1001;
  background-color: ${({ theme }) => theme.color.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};
`

const MobileInputButton = styled.div`
  border-radius: 16px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  width: 100%;

  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
`

type MobileTrackSearchProps = Pick<
  TrackSearchProps,
  | 'isLoading'
  | 'onItemClick'
  | 'onPlayClick'
  | 'tracks'
  | 'onSearch'
  | 'error'
  | 'defaultSearchValue'
> & {
  placeholder: string
  control: Control<SearchTrackFormData>
}

export const MobileTrackSearch: FC<MobileTrackSearchProps> = ({
  isLoading,
  onItemClick,
  onPlayClick,
  tracks = [],
  onSearch,
  error,
  defaultSearchValue,
  placeholder,
  control,
}) => {
  const [isMobileSearch, setIsMobileSearch] = useState(false)

  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH })

  if (!isMobile) {
    return null
  }

  const onEnterToMobileSearch = (): void => {
    setIsMobileSearch(true)
  }

  const onMobileBackButtonClick = (): void => {
    setIsMobileSearch(false)
  }

  return (
    <>
      <MobileInputButton onClick={onEnterToMobileSearch}>
        {placeholder}
      </MobileInputButton>
      <FullScreenModal open={isMobileSearch}>
        <MobileInputContainer>
          <StyledIconButton onClick={onMobileBackButtonClick}>
            <Arrow />
          </StyledIconButton>

          <Controller
            name='searchInput'
            control={control}
            defaultValue={defaultSearchValue || ''}
            render={({ field: { onChange, onBlur, value } }) => (
              <MobileSearchInput
                placeholder={placeholder}
                allowClear={{ clearIcon: <CloseIcon /> }}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onSearch(e.target.value)
                }
              />
            )}
          />
        </MobileInputContainer>
        {isLoading && (
          <TracksContainer>
            <Loading />
          </TracksContainer>
        )}

        {error && <TracksContainer>{error}</TracksContainer>}

        {tracks?.length && !error ? (
          <TracksContainer>
            <Results>
              <TrackAudioPlayer
                render={({ loading, isPlaying, onClick, currentTrackUrl }) => (
                  <>
                    {tracks.map((item) => (
                      <TrackItem
                        key={item.id}
                        onItemClick={() => onItemClick(item.id, item)}
                        trackName={item.name}
                        artistName={item.artistName}
                        trackDuration={item.trackDuration}
                        trackCoverUrl={item.trackCoverUrl}
                        loading={loading && currentTrackUrl === item.previewUrl}
                        onTrackControlClick={(e) => {
                          onClick(e, item.previewUrl)
                          if (!isPlaying) {
                            onPlayClick && onPlayClick()
                          }
                        }}
                        isPlaying={
                          isPlaying && currentTrackUrl === item.previewUrl
                        }
                        show={Boolean(item.previewUrl)}
                      />
                    ))}
                  </>
                )}
              />
            </Results>
          </TracksContainer>
        ) : null}
      </FullScreenModal>
    </>
  )
}
